import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IsolatedStageTimer, IsolatedStageTotalTimer } from "@/components/isolated-stage-timer";
import { Square, Clock, UserMinus, ArrowRightLeft, ChevronDown, X } from "lucide-react";
import type { Performer, JamNight } from "@shared/schema";
import SubstituteOutModal from "@/components/substitute-out-modal";
import { usePerformerMutations } from "@/hooks/use-performer-mutations";
import { useOnStagePerformers } from "@/hooks/use-on-stage-performers";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UnifiedOnStageTableProps {
  performers: Performer[];
  jamNightId?: number;
  jamNight?: JamNight;
  disabled?: boolean;
  mode: 'jam-night' | 'open-mic';
  forceMobileView?: boolean;
}

export default function UnifiedOnStageTable({
  performers,
  jamNightId,
  jamNight,
  disabled = false,
  mode,
  forceMobileView = false,
}: UnifiedOnStageTableProps) {
  const [showSubOutModal, setShowSubOutModal] = useState(false);
  const [selectedPerformer, setSelectedPerformer] = useState<Performer | null>(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  
  const onStagePerformers = useOnStagePerformers(performers);
  const { updatePerformerMutation, handleOffStage: baseHandleOffStage } = usePerformerMutations({ jamNightId });

  // Mode-specific configurations
  const isJamNight = mode === 'jam-night';
  const isOpenMic = mode === 'open-mic';

  // Handle instrument change - switch what they're currently playing on stage
  const handleInstrumentChange = (performerId: number, instrument: string) => {
    // Set single instrument as current instrument
    const newCurrentInstruments = [instrument];
    
    // Update performer's current instrument (what they're playing on stage now)
    // This does NOT modify their overall instruments list (what they CAN play)
    updatePerformerMutation.mutate({
      id: performerId,
      updates: {
        currentInstruments: newCurrentInstruments,
      },
    });
  };


  // Jam Night specific handlers
  const handleSubOut = (performer: Performer) => {
    setSelectedPerformer(performer);
    setShowSubOutModal(true);
  };

  const handleSubstituteOut = (replacementPerformerId?: number, selectedInstruments?: string[]) => {
    if (!selectedPerformer) return;

    if (replacementPerformerId) {
      updatePerformerMutation.mutate({
        id: replacementPerformerId,
        updates: {
          status: 'selected',
          replacingPerformerId: selectedPerformer.id,
          currentInstruments: selectedInstruments || [],
        },
      });

      updatePerformerMutation.mutate({
        id: selectedPerformer.id,
        updates: {
          status: 'pending_removal',
          replacementName: performers.find(p => p.id === replacementPerformerId)?.name || "",
        },
      });
    } else {
      updatePerformerMutation.mutate({
        id: selectedPerformer.id,
        updates: {
          status: 'pending_removal',
        },
      });
    }

    setSelectedPerformer(null);
  };

  const handleSwapPerformers = (performer: Performer) => {
    const replacement = performers.find(p => p.name === performer.replacementName && p.status === 'selected');
    if (!replacement) return;

    const elapsedMinutes = performer.stageStartTime
      ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
      : 0;

    updatePerformerMutation.mutate({
      id: replacement.id,
      updates: {
        status: 'on_stage',
        played: true,
        stageStartTime: new Date(),
        replacingPerformerId: null,
      },
    });

    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'participant',
        stageStartTime: null,
        timeOnStage: (performer.timeOnStage || 0) + elapsedMinutes,
        played: true,
        currentInstruments: [],
        replacementName: null,
      },
    });
  };

  // Handle immediate move to participant (waiting) status
  const handleImmediateToParticipant = (performer: Performer) => {
    const elapsedMinutes = performer.stageStartTime
      ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
      : 0;

    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'participant',
        stageStartTime: null,
        timeOnStage: (performer.timeOnStage || 0) + elapsedMinutes,
        played: true,
        currentInstruments: [],
        replacementName: null,
        replacingPerformerId: null,
      },
    });
  };

  // Handle clearing all performers from stage
  const handleClearAllFromStage = () => {
    onStagePerformers.forEach((performer) => {
      const elapsedMinutes = performer.stageStartTime
        ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
        : 0;

      updatePerformerMutation.mutate({
        id: performer.id,
        updates: {
          status: 'participant',
          stageStartTime: null,
          timeOnStage: (performer.timeOnStage || 0) + elapsedMinutes,
          played: true,
          currentInstruments: [],
          replacementName: null,
          replacingPerformerId: null,
        },
      });
    });
  };

  // Jam Night specific handleOffStage (extends base with replacement logic)
  const handleOffStage = (performer: Performer) => {
    if (isJamNight) {
      // Clear replacement data from any performers who were targeting this performer
      const performersTargetingThis = performers.filter(
        (p) => p.replacingPerformerId === performer.id,
      );
      performersTargetingThis.forEach((targetingPerformer) => {
        updatePerformerMutation.mutate({
          id: targetingPerformer.id,
          updates: {
            replacingPerformerId: null,
          },
        });
      });

      const elapsedMinutes = performer.stageStartTime
        ? Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000 / 60)
        : 0;

      console.log('[handleOffStage] Moving performer to participant status:', performer.name, performer.id);
      updatePerformerMutation.mutate({
        id: performer.id,
        updates: {
          status: 'participant',
          stageStartTime: null,
          timeOnStage: (performer.timeOnStage || 0) + elapsedMinutes,
          played: true,
          currentInstruments: [],
          replacementName: null,
        },
      });
    } else {
      // Open Mic: Use base handleOffStage
      baseHandleOffStage(performer);
    }
  };

  // Open Mic specific: time limit checking
  const isOverTime = (performerId: number) => {
    if (!isOpenMic || !jamNight || !jamNight.timeLimit) return false;
    const performer = onStagePerformers.find(p => p.id === performerId);
    if (!performer?.stageStartTime) return false;
    const elapsed = Math.floor((Date.now() - new Date(performer.stageStartTime).getTime()) / 1000);
    return elapsed / 60 > jamNight.timeLimit;
  };

  // Don't render if no performers are on stage
  if (onStagePerformers.length === 0) {
    return null;
  }

  return (
    <div className={`bg-green-100/30 backdrop-blur-xl rounded-2xl shadow-2xl border border-green-200/40 overflow-hidden ${isJamNight ? 'mb-2' : 'mb-6'} relative group`}
      style={{
        background: 'linear-gradient(135deg, rgba(34,197,94,0.25) 0%, rgba(34,197,94,0.1) 100%)',
        boxShadow: '0 8px 32px rgba(34,197,94,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}>
      {/* Liquid Glass Specular Highlight */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />
      
      <div className="px-6 py-4 border-b border-green-200/40 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-green-900/90 flex items-center drop-shadow-sm">
              <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse drop-shadow-sm"></div>
              On Stage
              <span className="ml-2 text-sm font-normal text-green-600/80 drop-shadow-sm">
                ({onStagePerformers.length})
              </span>
            </h2>
          </div>
          {onStagePerformers.length > 1 && isJamNight && (
            <Button
              size="sm"
              onClick={() => setShowClearConfirmation(true)}
              disabled={disabled || updatePerformerMutation.isPending}
              className="bg-red-600/80 backdrop-blur-sm hover:bg-red-700/90 text-white border border-red-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed h-7 px-3 text-xs"
              style={{
                boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear All ({onStagePerformers.length})
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className={forceMobileView ? "hidden" : "hidden lg:block overflow-x-auto relative z-10"}>
        <table className="min-w-full divide-y divide-green-200/30">
          <thead className="bg-green-100/40 backdrop-blur-sm">
            <tr>
              <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-green-700/80 uppercase tracking-wider drop-shadow-sm">
                Name
              </th>
              <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-green-700/80 uppercase tracking-wider drop-shadow-sm">
                {isJamNight ? 'Instrument' : 'Type'}
              </th>
              <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-green-700/80 uppercase tracking-wider drop-shadow-sm">
                Current Time
              </th>
              {isJamNight && (
                <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-green-700/80 uppercase tracking-wider drop-shadow-sm">
                  Total Time
                </th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-green-700/80 uppercase tracking-wider drop-shadow-sm">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/30 backdrop-blur-sm divide-y divide-green-200/30">
            {onStagePerformers.map((performer) => (
              <tr
                key={performer.id}
                className={`hover:bg-white/50 backdrop-blur-sm transition-all duration-300 ${
                  isJamNight 
                    ? (performer.status === 'pending_removal' ? "bg-red-100/30" : "")
                    : (isOverTime(performer.id) ? 'bg-red-100/30' : '')
                }`}
                style={{
                  boxShadow: (isJamNight ? performer.status === 'pending_removal' : isOverTime(performer.id)) 
                    ? '0 2px 8px rgba(220,38,38,0.1)' 
                    : undefined,
                }}
              >
                {/* Name Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
                        {performer.name}
                      </div>
                      {/* Jam Night: Pending removal or replacement info */}
                      {isJamNight && performer.status === 'pending_removal' && (
                        <div className="mt-2 p-2 bg-orange-100/40 backdrop-blur-sm border border-orange-200/50 rounded-md" style={{
                          boxShadow: '0 2px 8px rgba(251,146,60,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                        }}>
                          <div className="flex items-center space-x-2">
                            <UserMinus className="h-3 w-3 text-orange-600 drop-shadow-sm" />
                            {performer.replacementName ? (
                              <>
                                <span className="text-sm font-medium text-orange-700/90 drop-shadow-sm">
                                  Replace with
                                </span>
                                <span className="text-sm font-bold text-orange-800/90 drop-shadow-sm">
                                  {performer.replacementName}
                                </span>
                                {(() => {
                                  const replacement = performers.find(p => p.name === performer.replacementName && p.status === 'selected');
                                  return replacement && replacement.currentInstruments && replacement.currentInstruments.length > 0 && (
                                    <Badge variant="outline" className="text-xs bg-orange-100/60 backdrop-blur-sm border-orange-300/60 text-orange-700/90">
                                      {replacement.currentInstruments.join(", ")}
                                    </Badge>
                                  );
                                })()}
                              </>
                            ) : (
                              <span className="text-sm font-medium text-orange-700/90 drop-shadow-sm">
                                Pending Removal
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Second Column: Instruments (Jam Night) or Type (Open Mic) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {isJamNight ? (
                      performer.instruments?.length ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild disabled={disabled || updatePerformerMutation.isPending}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto px-2 py-1 bg-green-100/60 backdrop-blur-sm text-green-800/90 border border-green-200/50 hover:bg-green-200/60 transition-all duration-200"
                              style={{
                                boxShadow: '0 2px 8px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                              }}
                            >
                              <span className="text-sm font-medium">
                                {performer.currentInstruments?.length ? performer.currentInstruments[0] : 'Select Instrument'}
                              </span>
                              <ChevronDown className="ml-1 h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-48">
                            <DropdownMenuRadioGroup 
                              value={(performer.currentInstruments || [])[0] || ""} 
                              onValueChange={(value) => handleInstrumentChange(performer.id, value)}
                            >
                              {performer.instruments.map((instrument) => (
                                <DropdownMenuRadioItem key={instrument} value={instrument}>
                                  {instrument}
                                </DropdownMenuRadioItem>
                              ))}
                            </DropdownMenuRadioGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-sm text-slate-500/80 italic drop-shadow-sm">
                          No instruments registered
                        </span>
                      )
                    ) : (
                      <Badge variant="outline" className="capitalize bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50" style={{
                        boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}>
                        {performer.performerType || 'solo'}
                      </Badge>
                    )}
                  </div>
                </td>

                {/* Performance Time Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-orange-600/90">
                    <Clock className={`mr-2 drop-shadow-sm ${isJamNight ? 'h-5 w-5' : 'h-4 w-4 text-warning'}`} />
                    <IsolatedStageTimer 
                      stageStartTime={performer.stageStartTime}
                      className={
                        isJamNight 
                          ? "font-mono font-bold text-2xl drop-shadow-sm"
                          : `font-mono text-lg font-medium drop-shadow-sm ${isOverTime(performer.id) ? 'text-red-500/90' : 'text-warning'}`
                      }
                    />
                  </div>
                </td>

                {/* Total Time Column (Jam Night only) */}
                {isJamNight && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-blue-600/90">
                      <Clock className="h-4 w-4 mr-2 drop-shadow-sm" />
                      <IsolatedStageTotalTimer 
                        stageStartTime={performer.stageStartTime}
                        previousTimeOnStage={performer.timeOnStage || 0}
                        className="font-mono font-medium text-lg drop-shadow-sm"
                      />
                    </div>
                  </td>
                )}

                {/* Actions Column */}
                <td className={`px-6 py-4 whitespace-nowrap ${isOpenMic ? 'text-sm text-slate-500 space-x-2' : ''}`}>
                  {isJamNight ? (
                    performer.status === 'pending_removal' ? (
                      <div className="flex space-x-2 justify-end">
                        {performer.replacementName && (
                          <Button
                            size="sm"
                            onClick={() => handleSwapPerformers(performer)}
                            disabled={disabled || updatePerformerMutation.isPending}
                            className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                            }}
                          >
                            <ArrowRightLeft className="h-3 w-3 mr-1" />
                            Swap
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleOffStage(performer)}
                          disabled={disabled || updatePerformerMutation.isPending}
                          className="bg-red-600/80 backdrop-blur-sm text-white hover:bg-red-700/90 border border-red-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }}
                        >
                          <Square className="h-3 w-3 mr-1" />
                          {performer.replacementName ? "Off" : "Off Stage"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex space-x-2 justify-end">
                        <Button
                          size="sm"
                          onClick={() => handleSubOut(performer)}
                          disabled={updatePerformerMutation.isPending}
                          className="bg-orange-600/80 backdrop-blur-sm text-white hover:bg-orange-700/90 border border-orange-500/30 shadow-lg transition-all duration-300"
                          style={{
                            boxShadow: '0 4px 16px rgba(251,146,60,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }}
                        >
                          <UserMinus className="h-3 w-3 mr-1" />
                          Sub Out
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleImmediateToParticipant(performer)}
                          disabled={disabled || updatePerformerMutation.isPending}
                          className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                          style={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleOffStage(performer)}
                      disabled={disabled || updatePerformerMutation.isPending}
                      className="bg-red-500/80 backdrop-blur-sm hover:bg-red-600/90 border border-red-400/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        boxShadow: '0 4px 16px rgba(239,68,68,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}
                    >
                      <Square className="h-3 w-3 mr-1" />
                      Off Stage
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className={forceMobileView ? "block relative z-10" : "block lg:hidden relative z-10"}>
        {isJamNight ? (
          // Jam Night Mobile Cards
          <div className="divide-y divide-green-200/30">
            {onStagePerformers.map((performer) => (
              <div
                key={performer.id}
                className={`p-4 backdrop-blur-sm transition-all duration-300 ${performer.status === 'pending_removal' ? "bg-red-100/30" : "bg-white/30"}`}
                style={{
                  boxShadow: performer.status === 'pending_removal' ? '0 2px 8px rgba(220,38,38,0.1)' : undefined,
                }}
              >
                <div>
                  {/* Top row: Name and action buttons */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <h3 className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
                          {performer.name}
                        </h3>
                        
                        {/* Instrument selector below name */}
                        {performer.instruments?.length ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={disabled || updatePerformerMutation.isPending}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 bg-green-100/60 backdrop-blur-sm text-green-800/90 border border-green-200/50 hover:bg-green-200/60 transition-all duration-200"
                                style={{
                                  boxShadow: '0 2px 8px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                                }}
                              >
                                <span className="text-xs font-medium">
                                  {performer.currentInstruments?.length ? performer.currentInstruments[0] : 'Select Instrument'}
                                </span>
                                <ChevronDown className="ml-1 h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-48">
                              <DropdownMenuRadioGroup 
                                value={(performer.currentInstruments || [])[0] || ""} 
                                onValueChange={(value) => handleInstrumentChange(performer.id, value)}
                              >
                                {performer.instruments.map((instrument) => (
                                  <DropdownMenuRadioItem key={instrument} value={instrument}>
                                    {instrument}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </div>
                    </div>

                    {/* Action buttons on the right */}
                    <div className="ml-4 flex gap-2">
                      {performer.status === 'pending_removal' ? (
                        <>
                          {performer.replacementName && (
                            <Button
                              size="sm"
                              onClick={() => handleSwapPerformers(performer)}
                              disabled={updatePerformerMutation.isPending}
                              className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300"
                              style={{
                                boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                              }}
                            >
                              <ArrowRightLeft className="h-3 w-3 mr-1" />
                              Swap
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleOffStage(performer)}
                            disabled={disabled || updatePerformerMutation.isPending}
                            className="bg-red-600/80 backdrop-blur-sm text-white hover:bg-red-700/90 border border-red-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                            }}
                          >
                            <Square className="h-3 w-3 mr-1" />
                            {performer.replacementName ? "Off" : "Off"}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleSubOut(performer)}
                            disabled={disabled || updatePerformerMutation.isPending}
                            className="bg-orange-600/80 backdrop-blur-sm text-white hover:bg-orange-700/90 border border-orange-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              boxShadow: '0 4px 16px rgba(251,146,60,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                            }}
                          >
                            <UserMinus className="h-3 w-3 mr-1" />
                            Sub Out
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleImmediateToParticipant(performer)}
                            disabled={disabled || updatePerformerMutation.isPending}
                            className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                            style={{
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Full-width sections below */}
                  {/* Performance times */}
                  <div className="space-y-1">
                    <div className="flex items-center text-orange-600">
                      <Clock className="h-4 w-4 mr-2 drop-shadow-sm" />
                      <span className="text-xs text-slate-600/80 mr-2 drop-shadow-sm">Current:</span>
                      <IsolatedStageTimer 
                        stageStartTime={performer.stageStartTime}
                        className="font-mono font-bold text-lg drop-shadow-sm"
                      />
                    </div>
                    <div className="flex items-center text-blue-600">
                      <Clock className="h-4 w-4 mr-2 drop-shadow-sm" />
                      <span className="text-xs text-slate-600/80 mr-2 drop-shadow-sm">Total:</span>
                      <IsolatedStageTotalTimer 
                        stageStartTime={performer.stageStartTime}
                        previousTimeOnStage={performer.timeOnStage || 0}
                        className="font-mono font-medium text-sm drop-shadow-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Pending removal or replacement info */}
                  {performer.status === 'pending_removal' && (
                    <div className="mt-2 p-2 bg-orange-100/40 backdrop-blur-sm border border-orange-200/50 rounded-md" style={{
                      boxShadow: '0 2px 8px rgba(251,146,60,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}>
                      <div className="flex items-center space-x-2 flex-wrap">
                        <UserMinus className="h-3 w-3 text-orange-600 drop-shadow-sm" />
                        {performer.replacementName ? (
                          <>
                            <span className="text-sm font-medium text-orange-700/90 drop-shadow-sm">
                              Replace with
                            </span>
                            <span className="text-sm font-bold text-orange-800/90 drop-shadow-sm">
                              {performer.replacementName}
                            </span>
                            {(() => {
                              const replacement = performers.find(p => p.name === performer.replacementName && p.status === 'selected');
                              return replacement && replacement.currentInstruments && replacement.currentInstruments.length > 0 && (
                                <Badge variant="outline" className="text-xs bg-orange-100/60 backdrop-blur-sm border-orange-300/60 text-orange-700/90">
                                  {replacement.currentInstruments.join(", ")}
                                </Badge>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="text-sm font-medium text-orange-700/90 drop-shadow-sm">
                            Pending Removal
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Open Mic Mobile Cards
          onStagePerformers.map((performer) => (
            <div
              key={performer.id}
              className={`border-b border-green-200/30 last:border-b-0 p-4 backdrop-blur-sm transition-all duration-300 ${isOverTime(performer.id) ? 'bg-red-100/30' : 'bg-white/30'}`}
              style={{
                boxShadow: isOverTime(performer.id) ? '0 2px 8px rgba(220,38,38,0.1)' : undefined,
              }}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm mb-1">
                    {performer.name}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <Badge variant="outline" className="capitalize bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50" style={{
                      boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}>
                      {performer.performerType || 'solo'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-warning mr-2 drop-shadow-sm" />
                  <IsolatedStageTimer 
                    stageStartTime={performer.stageStartTime}
                    className={`font-mono text-lg font-medium drop-shadow-sm ${isOverTime(performer.id) ? 'text-red-500/90' : 'text-warning'}`}
                  />
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleOffStage(performer)}
                  disabled={disabled || updatePerformerMutation.isPending}
                  className="bg-red-500/80 backdrop-blur-sm hover:bg-red-600/90 border border-red-400/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    boxShadow: '0 4px 16px rgba(239,68,68,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <Square className="h-3 w-3 mr-1" />
                  Off Stage
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Jam Night Substitute Modal */}
      {isJamNight && selectedPerformer && (
        <SubstituteOutModal
          open={showSubOutModal}
          onOpenChange={setShowSubOutModal}
          performerToSubOut={selectedPerformer}
          availableReplacements={performers}
          onConfirm={handleSubstituteOut}
        />
      )}

      {/* Clear All Confirmation Dialog */}
      <AlertDialog open={showClearConfirmation} onOpenChange={setShowClearConfirmation}>
        <AlertDialogContent className="bg-white/95 backdrop-blur-xl border border-white/30">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Performers from Stage?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move all {onStagePerformers.length} performers currently on stage back to the waiting list. 
              Their stage time will be recorded. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAllFromStage}
              className="bg-red-600/80 backdrop-blur-sm text-white hover:bg-red-700/90 border border-red-500/30"
              style={{
                boxShadow: '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}